// ============================================================================
// Guided lesson runner (spec §6.1): (1) INTRODUCE new hanja + words as rich
// cards, (2) PRACTICE them with a short game mix (reusing the games registry),
// writing each result via record_review, (3) COMPLETE and point to the next
// lesson. Studying every item is what marks the lesson complete on the path.
// ============================================================================

import { el, mount, toast } from '../dom.js';
import * as api from '@core/data/api.js';
import { romanize } from '@core/hangul/romanize.js';
import { GAME, SKILL, ITEM_TYPE } from '@core/models/constants.js';
import { gameById } from '../games/index.js';
import { romanizationOn } from '../prefs.js';
import { speakerButton } from '../audio.js';

// Adapt a lesson_detail item into the shape the games expect.
function toContent(it) {
  if (it.item_type === ITEM_TYPE.CHARACTER) {
    return { id: it.item_id, char: it.char, meaning_en: it.meaning_en,
             character_readings: [{ hun: it.hun, eum: it.eum, is_primary: true }] };
  }
  return { id: it.item_id, hangul: it.hangul, hanja_form: it.hanja_form,
           meaning_en: it.meaning_en, romanization: it.romanization };
}

export function renderLesson({ navigate, id }) {
  const container = el('div.container.stack');
  mount(container, el('div.card', { text: 'Loading lesson…' }));

  let lesson = null, items = [], pool = { chars: [], words: [] };
  let phase = 'intro', idx = 0;
  const tally = { reviewed: 0, xp: 0 };

  (async () => {
    try {
      const [detail, chars, words] = await Promise.all([
        api.lessonDetail(id),
        api.listCharacters({ limit: 400 }),
        api.listWords({ limit: 1000 }),
      ]);
      lesson = detail.lesson;
      items = detail.items || [];
      pool = { chars, words };
      if (!items.length) return mount(container, backCard('This lesson has no items yet.'));
      paint();
    } catch (e) {
      mount(container, backCard(e.message || 'Could not load lesson'));
    }
  })();

  function backCard(msg) {
    return el('div.card.stack', {}, [
      el('div.banner.banner--warn', { text: msg }),
      el('button.btn', { text: '← Guided path', onclick: () => navigate('#/path') }),
    ]);
  }

  function progressHeader(label) {
    return el('div.row', {}, [
      el('span.faint', { text: lesson?.title || 'Lesson' }),
      el('div.appbar__spacer'),
      el('span.faint', { text: label }),
    ]);
  }

  // --- INTRODUCE ------------------------------------------------------------
  function introCard(it) {
    const isChar = it.item_type === ITEM_TYPE.CHARACTER;
    const audio = isChar ? it.eum : it.hangul;
    const rom = isChar ? (it.eum ? romanize(it.eum) : '') : (it.romanization || romanize(it.hangul || ''));
    return el('div.card.stack.center', {}, [
      el('div.faint', { text: isChar ? 'New hanja' : 'New word' }),
      isChar
        ? el('div.flashcard__char', { text: it.char })
        : el('div.stack.center', {}, [el('div.flashcard__word', { text: it.hangul }), el('div.faint', { text: it.hanja_form || '' })]),
      el('div.row.center', { style: 'justify-content:center' }, [speakerButton(() => audio, { label: '🔊 Listen' })]),
      el('div.flashcard__answer', { text: isChar ? `${it.hun ?? ''} ${it.eum ?? ''}`.trim() : (it.meaning_en || '') }),
      el('div.muted', { text: isChar ? (it.meaning_en || '') : '' }),
      rom && romanizationOn() ? el('div.faint', { text: rom }) : false,
    ]);
  }

  function paintIntro() {
    const it = items[idx];
    const last = idx === items.length - 1;
    mount(container, el('div.card.stack', {}, [
      progressHeader(`Introduce ${idx + 1} / ${items.length}`),
      introCard(it),
      el('div.row', {}, [
        idx > 0 ? el('button.btn', { text: '← Back', onclick: () => { idx -= 1; paint(); } }) : false,
        el('div.appbar__spacer'),
        el('button.btn.btn--primary', {
          text: last ? 'Practice these →' : 'Next →',
          onclick: () => { if (last) { phase = 'practice'; idx = 0; } else { idx += 1; } paint(); },
        }),
      ]),
    ]));
  }

  // --- PRACTICE -------------------------------------------------------------
  async function onGrade({ grade, isCorrect, responseMs = null }) {
    const it = items[idx];
    try {
      const res = await api.recordReview({
        itemType: it.item_type, itemId: it.item_id, skill: SKILL.RECOGNITION,
        gameType: currentGameId(idx), grade, isCorrect, responseMs,
      });
      tally.reviewed += 1;
      if (res?.points_earned) { tally.xp += res.points_earned; toast(`+${res.points_earned} XP`, 'success'); }
    } catch (e) {
      toast(e.message || 'Could not save review', 'error');
      if (/activated/i.test(e.message || '')) return navigate('#/');
    }
    idx += 1;
    phase = idx >= items.length ? 'done' : 'practice';
    paint();
  }

  // Alternate two games for variety; both apply to characters and words.
  function currentGameId(i) {
    return i % 2 === 0 ? GAME.MULTIPLE_CHOICE : GAME.FLASHCARDS;
  }

  function paintPractice() {
    const it = items[idx];
    const game = gameById(currentGameId(idx));
    const body = game.build({
      item: { item_type: it.item_type, item_id: it.item_id, skill: SKILL.RECOGNITION, mastery_state: 'new' },
      content: toContent(it), pool, onGrade,
    });
    mount(container, el('div.card.stack', {}, [
      progressHeader(`Practice ${idx + 1} / ${items.length} · ${game.label}`),
      body,
    ]));
  }

  // --- DONE -----------------------------------------------------------------
  async function paintDone() {
    mount(container, el('div.card.stack.center', {}, [
      el('h2', { text: 'Lesson complete 🎉' }),
      el('p.muted', { text: `Reviewed ${tally.reviewed} item${tally.reviewed === 1 ? '' : 's'} · +${tally.xp} XP` }),
      el('div.row.center', {}, [
        el('button.btn.btn--primary', { text: 'Next lesson →', onclick: () => navigate('#/path') }),
        el('button.btn', { text: 'Review again', onclick: () => { phase = 'intro'; idx = 0; tally.reviewed = 0; tally.xp = 0; paint(); } }),
      ]),
    ]));
  }

  function paint() {
    if (phase === 'intro') return paintIntro();
    if (phase === 'practice') return paintPractice();
    return paintDone();
  }

  return container;
}
